import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const SceneExit = ({ selectedPackage, selectedUpgrades = [], total, onCheckout }) => {
  const sectionRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });
  
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.8, 1], [0, 1, 1, 0]);

  const hasSelection = selectedPackage !== null;

  // Smoother, more cinematic easing
  const smoothEase = [0.4, 0, 0.2, 1];

  return (
    <section 
      ref={sectionRef}
      className="scene relative min-h-screen flex items-center justify-center py-32 overflow-hidden"
      id="checkout"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-background" />
      
      {/* Subtle radial glow - very subtle */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_hsla(38,50%,60%,0.03)_0%,_transparent_50%)]" />
      
      <motion.div 
        className="relative z-10 w-full max-w-3xl mx-auto px-6 text-center"
        style={{ opacity }}
      >
        {hasSelection ? (
          <>
            {/* Ready state - Confident, clear */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: smoothEase }}
              viewport={{ once: true }}
            >
              <p className="font-body text-sm tracking-[0.3em] text-accent uppercase mb-6">
                READY
              </p>
              <h2 className="font-display text-hero text-foreground mb-10">
                COMPLETE YOUR ORDER
              </h2>
            </motion.div>
            
            {/* Order summary - Clean, grounded */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15, ease: smoothEase }}
              viewport={{ once: true }}
              className="bg-card border border-border p-8 md:p-10 mb-10 text-left"
            >
              {/* Package */}
              <div className="flex justify-between items-start mb-6 pb-6 border-b border-border/50">
                <div>
                  <p className="font-body text-xs text-accent tracking-wider mb-2">FORMULA {selectedPackage.formula}</p>
                  <p className="font-display text-xl text-foreground">{selectedPackage.name}</p>
                </div>
                <span className="font-display text-2xl text-foreground">${selectedPackage.price}</span>
              </div>
              
              {/* Upgrades */}
              {selectedUpgrades.length > 0 && (
                <div className="space-y-2 mb-6 pb-6 border-b border-border/50">
                  <p className="font-body text-xs text-accent/80 tracking-wider mb-3">UPGRADES</p>
                  {selectedUpgrades.map(upgrade => (
                    <div key={upgrade.id} className="flex justify-between items-center">
                      <span className="font-body text-sm text-muted-foreground">{upgrade.name}</span>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Total */}
              <div className="flex justify-between items-center pt-2">
                <span className="font-display text-lg text-foreground">TOTAL</span>
                <span className="font-display text-4xl text-accent">${total}</span>
              </div>
            </motion.div>
            
            {/* CTA - Natural next step */}
            <motion.button
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3, ease: smoothEase }}
              viewport={{ once: true }}
              onClick={onCheckout}
              className="btn-command text-xl px-12 py-5"
            >
              CHECKOUT <ArrowRight className="inline w-5 h-5 ml-3" />
            </motion.button>
          </>
        ) : (
          <>
            {/* No selection state - Calm guidance */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: smoothEase }}
              viewport={{ once: true }}
            >
              <p className="font-body text-sm tracking-[0.3em] text-muted-foreground/60 uppercase mb-6">
                NEXT STEP
              </p>
              <h2 className="font-display text-hero text-foreground mb-8">
                SELECT A PACKAGE
              </h2>
              <p className="font-body text-base text-muted-foreground/70 mb-10 max-w-md mx-auto leading-relaxed">
                Choose your path and select a formula to continue.
              </p>
              
              <button
                onClick={() => document.getElementById('identity-gate')?.scrollIntoView({ behavior: 'smooth' })}
                className="btn-command-outline"
              >
                CHOOSE YOUR PATH
              </button>
            </motion.div>
          </>
        )}
        
        {/* Footer note - Quiet confidence */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5, ease: smoothEase }}
          viewport={{ once: true }}
          className="font-body text-xs text-muted-foreground/40 mt-20"
        >
          Secure payment via Stripe
        </motion.p>
      </motion.div>
    </section>
  );
};

export default SceneExit;
